import type { PublicShowcaseApplication } from '../shared/types'

export interface ShowcaseDemo {
  slug: string
  name: string
  challenge?: number | null
  featured?: boolean
  tagline: string
  app: string
  demo: string
  pitch?: string
  devpost?: string
  preview: string
  poster: string
  film: string
  durationSeconds: number
}

const MEDIA_ORIGIN = 'https://hackathon.sasid.ai'

type DemoSource = Omit<ShowcaseDemo, 'preview' | 'poster' | 'film'> & {
  preview: string
  poster: string
}

const sources: DemoSource[] = [
  {
    slug: 'tiersel',
    name: 'Tiersel Houston',
    challenge: null,
    featured: true,
    tagline:
      'Ask Houston’s 311 data a question, out loud or typed, and see a verified answer with charts, maps and the work behind it.',
    app: 'https://houston.tiersel.com',
    demo: 'https://houston.tiersel.com/demo',
    pitch: 'https://houston.tiersel.com/pitch',
    devpost: 'https://devpost.com/software/tiersel-houston-57uevi',
    preview: 'public/previews/tiersel.mp4',
    poster: 'public/previews/tiersel.webp',
    durationSeconds: 100,
  },
  {
    slug: 'cleanhou',
    name: 'CleanHOU',
    challenge: 1,
    featured: false,
    tagline:
      'Turn Houston 311 cleanup reports into neighborhood action: find a hotspot, claim it, invite a crew, and record completion.',
    app: 'https://cleanhou.sasid.ai/',
    demo: 'https://cleanhou.sasid.ai/demo',
    pitch: 'https://cleanhou.sasid.ai/pitch',
    devpost: 'https://devpost.com/software/cleanhou',
    preview: 'public/previews/cleanhou.mp4',
    poster: 'public/previews/cleanhou.webp',
    durationSeconds: 60,
  },
  {
    slug: 'spaceshou',
    name: 'SpacesHOU',
    challenge: 2,
    featured: false,
    tagline:
      'Find Houston gathering spaces by neighborhood, capacity, cost, and amenities, then keep a source-backed shortlist.',
    app: 'https://spaceshou.sasid.ai/',
    demo: 'https://spaceshou.sasid.ai/demo/',
    pitch: 'https://spaceshou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/spaceshou',
    preview: 'public/previews/spaceshou.mp4',
    poster: 'public/previews/spaceshou.webp',
    durationSeconds: 57,
  },
  {
    slug: 'sandboxhou',
    name: 'SandboxHOU',
    challenge: 3,
    featured: false,
    tagline:
      'Help Houston founders turn a focused MVP question into a small test and structured feedback.',
    app: 'https://sandboxhou.sasid.ai/',
    demo: 'https://sandboxhou.sasid.ai/demo',
    pitch: 'https://sandboxhou.sasid.ai/pitch',
    devpost: 'https://devpost.com/software/sandboxhou',
    preview: 'public/previews/sandboxhou.mp4',
    poster: 'public/previews/sandboxhou.webp',
    durationSeconds: 53,
  },
  {
    slug: 'coolhou',
    name: 'CoolHOU',
    challenge: 4,
    featured: false,
    tagline:
      'Find free indoor Houston places by hours, transit, and source-linked details when heat or rain changes the day.',
    app: 'https://coolhou.sasid.ai/',
    demo: 'https://coolhou.sasid.ai/demo/',
    pitch: 'https://coolhou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/coolhou',
    preview: 'public/previews/coolhou.mp4',
    poster: 'public/previews/coolhou.webp',
    durationSeconds: 63,
  },
  {
    slug: 'readyhou',
    name: 'ReadyHOU',
    challenge: 5,
    featured: false,
    tagline:
      'Prepare for emergencies with a multilingual resource directory, device-local plan, accessible guides, and low-bandwidth mode.',
    app: 'https://readyhou.sasid.ai/',
    demo: 'https://readyhou.sasid.ai/demo/',
    pitch: 'https://readyhou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/readyhou',
    preview: 'public/previews/readyhou.mp4',
    poster: 'public/previews/readyhou.webp',
    durationSeconds: 56,
  },
  {
    slug: 'accesshou',
    name: 'AccessHOU',
    challenge: 6,
    featured: false,
    tagline:
      'Find Houston adaptive resources with functional-need filters, explicit access details, source links, and local shortlists.',
    app: 'https://accesshou.sasid.ai/',
    demo: 'https://accesshou.sasid.ai/demo',
    pitch: 'https://accesshou.sasid.ai/pitch',
    devpost: 'https://devpost.com/software/accesshou',
    preview: 'public/previews/accesshou.mp4',
    poster: 'public/previews/accesshou.webp',
    durationSeconds: 60,
  },
  {
    slug: 'dayburn',
    name: 'DayBurn',
    challenge: 7,
    featured: false,
    tagline:
      'Report streetlights burning in daylight, follow the status, and explore transparent energy and cost estimates.',
    app: 'https://dayburn.sasid.ai/',
    demo: 'https://dayburn.sasid.ai/demo/',
    pitch: 'https://dayburn.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/dayburn',
    preview: 'public/previews/dayburn.mp4',
    poster: 'public/previews/dayburn.webp',
    durationSeconds: 65,
  },
  {
    slug: 'cobuyhou',
    name: 'CoBuyHOU',
    challenge: 8,
    featured: false,
    tagline:
      'Explore co-buying with a private brief, coarse opt-in discovery, and a planning room for two to four people.',
    app: 'https://cobuyhou.sasid.ai/',
    demo: 'https://cobuyhou.sasid.ai/demo/',
    pitch: 'https://cobuyhou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/cobuyhou',
    preview: 'public/previews/cobuyhou.mp4',
    poster: 'public/previews/cobuyhou.webp',
    durationSeconds: 68,
  },
  {
    slug: 'ridehou',
    name: 'RideHou',
    challenge: 9,
    featured: false,
    tagline:
      'Explore recurring work commutes by broad Houston zones, days, and shifts while keeping exact addresses private.',
    app: 'https://ridehou.sasid.ai/',
    demo: 'https://ridehou.sasid.ai/demo/',
    pitch: 'https://ridehou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/ridehou',
    preview: 'public/previews/ridehou.mp4',
    poster: 'public/previews/ridehou.webp',
    durationSeconds: 57,
  },
  {
    slug: 'seniorhou',
    name: 'SeniorHOU',
    challenge: 10,
    featured: false,
    tagline:
      'Give Houston families a calm starting point for a senior move: questions, a six-step route, options, and official resources.',
    app: 'https://seniorhou.sasid.ai/',
    demo: 'https://seniorhou.sasid.ai/demo/',
    pitch: 'https://seniorhou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/seniorhou',
    preview: 'public/previews/seniorhou.mp4',
    poster: 'public/previews/seniorhou.webp',
    durationSeconds: 66,
  },
  {
    slug: 'safehou',
    name: 'SafeHOU',
    challenge: 11,
    featured: false,
    tagline:
      'Turn Houston 311 street-service signals and resident observations into a transparent neighborhood conversation.',
    app: 'https://safehou.sasid.ai/',
    demo: 'https://safehou.sasid.ai/demo/',
    pitch: 'https://safehou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/1448014',
    preview: 'public/previews/safehou.mp4',
    poster: 'public/previews/safehou.webp',
    durationSeconds: 50,
  },
  {
    slug: 'snaphou',
    name: 'SnapHOU',
    challenge: 12,
    featured: false,
    tagline:
      'Help Foster Place turn approximate sidewalk observations into a local block-level aging-in-place plan.',
    app: 'https://snaphou.sasid.ai/',
    demo: 'https://snaphou.sasid.ai/demo/',
    pitch: 'https://snaphou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/1448017',
    preview: 'public/previews/snaphou.mp4',
    poster: 'public/previews/snaphou.webp',
    durationSeconds: 60,
  },
  {
    slug: 'nexthou',
    name: 'NexTHOU',
    challenge: 13,
    featured: false,
    tagline:
      'Help experienced Houston professionals see how capabilities can carry into curated paths across a changing economy.',
    app: 'https://nexthou.sasid.ai/',
    demo: 'https://nexthou.sasid.ai/demo/',
    pitch: 'https://nexthou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/1448019',
    preview: 'public/previews/nexthou.mp4',
    poster: 'public/previews/nexthou.webp',
    durationSeconds: 56,
  },
  {
    slug: 'civicboost',
    name: 'Civic Boost',
    challenge: 14,
    featured: false,
    tagline:
      'Turn a neighborhood need into a clear proposal, a community support signal, and the next official civic route.',
    app: 'https://civicboost.sasid.ai/',
    demo: 'https://civicboost.sasid.ai/demo',
    pitch: 'https://civicboost.sasid.ai/pitch',
    devpost: 'https://devpost.com/software/1448023',
    preview: 'public/previews/civicboost.mp4',
    poster: 'public/previews/civicboost.webp',
    durationSeconds: 50,
  },
  {
    slug: 'votehou',
    name: 'VoteHou',
    challenge: 15,
    featured: false,
    tagline:
      'Turn “I should vote” into a practical, nonpartisan plan with source-linked steps and local logistics.',
    app: 'https://votehou.sasid.ai/',
    demo: 'https://votehou.sasid.ai/demo/',
    pitch: 'https://votehou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/1448038',
    preview: 'public/previews/votehou.mp4',
    poster: 'public/previews/votehou.webp',
    durationSeconds: 65,
  },
  {
    slug: 'carehou',
    name: 'CareHOU',
    challenge: 16,
    featured: false,
    tagline:
      'Help families organize questions, handoffs, and practical responsibilities after a major health event.',
    app: 'https://carehou.sasid.ai/',
    demo: 'https://carehou.sasid.ai/demo',
    pitch: 'https://carehou.sasid.ai/pitch',
    devpost: 'https://devpost.com/software/1448044',
    preview: 'public/previews/carehou.mp4',
    poster: 'public/previews/carehou.webp',
    durationSeconds: 56,
  },
  {
    slug: 'storyhou',
    name: 'StoryHOU',
    challenge: 17,
    featured: false,
    tagline:
      'Give people a private, text-first place to author and preserve their own stories, including movement into and from Houston.',
    app: 'https://storyhou.sasid.ai/',
    demo: 'https://storyhou.sasid.ai/demo/',
    pitch: 'https://storyhou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/1448053',
    preview: 'public/previews/storyhou.mp4',
    poster: 'public/previews/storyhou.webp',
    durationSeconds: 45,
  },
  {
    slug: 'thirdhou',
    name: 'ThirdHOU',
    challenge: 18,
    featured: false,
    tagline:
      'Compare welcoming Houston parks, gardens, and community centers, then sketch the gathering place your neighborhood needs.',
    app: 'https://thirdhou.sasid.ai/',
    demo: 'https://thirdhou.sasid.ai/demo/',
    pitch: 'https://thirdhou.sasid.ai/pitch/',
    devpost: 'https://devpost.com/software/1448271',
    preview: 'public/previews/thirdhou.mp4',
    poster: 'public/previews/thirdhou.webp',
    durationSeconds: 55,
  },
]

export const HACKATHON_DEMOS: ShowcaseDemo[] = sources.map((source) => ({
  ...source,
  preview: `${MEDIA_ORIGIN}/${source.preview}`,
  poster: `${MEDIA_ORIGIN}/${source.poster}`,
  film: `${MEDIA_ORIGIN}/films/${source.slug}.mp4`,
}))

const demoBySlug = new Map(HACKATHON_DEMOS.map((demo) => [demo.slug, demo]))

// Challenge entries use the same slugs as their public health registry ids;
// the separate portfolio Tiersel entry is resolved below.
const slugByApplicationId: Record<string, string> = {
  cleanhou: 'cleanhou',
  spaceshou: 'spaceshou',
  sandboxhou: 'sandboxhou',
  coolhou: 'coolhou',
  readyhou: 'readyhou',
  accesshou: 'accesshou',
  dayburn: 'dayburn',
  cobuyhou: 'cobuyhou',
  ridehou: 'ridehou',
  seniorhou: 'seniorhou',
  safehou: 'safehou',
  snaphou: 'snaphou',
  nexthou: 'nexthou',
  civicboost: 'civicboost',
  votehou: 'votehou',
  carehou: 'carehou',
  storyhou: 'storyhou',
  thirdhou: 'thirdhou',
}

export const HOUSTON_TIERSEL_DEMO = HACKATHON_DEMOS[0]

export function hackathonDemoFor(
  application: Pick<PublicShowcaseApplication, 'id'>,
): ShowcaseDemo | undefined {
  const slug = slugByApplicationId[application.id]
  return slug ? demoBySlug.get(slug) : undefined
}

const generalDemosByApplicationId: Record<string, ShowcaseDemo> = {
  jevvy: {
    slug: 'tiersel-launch',
    name: 'Tiersel',
    tagline: 'Voice-first assistant that builds the interface while you speak.',
    app: 'https://tiersel.com',
    demo: 'https://tiersel.com/demo',
    pitch: 'https://tiersel.com/pitch',
    preview: 'https://tiersel.com/tiersel-media/tiersel-launch.mp4',
    poster: 'https://tiersel.com/tiersel-media/poster-tiersel-launch.jpg',
    film: 'https://tiersel.com/tiersel-media/tiersel-launch.mp4',
    durationSeconds: 88,
  },
}

export function showcaseDemoFor(
  application: Pick<PublicShowcaseApplication, 'id'>,
): ShowcaseDemo | undefined {
  return generalDemosByApplicationId[application.id] ?? hackathonDemoFor(application)
}
